import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, Label, Form
} from "reactstrap";
import { FiRefreshCcw, FiPlus, FiTrendingDown, FiPieChart, FiBarChart2, FiSearch } from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI DatePicker
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const CreditNoteReportList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [rawResponse, setRawResponse] = useState(null);
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 });

    const [filters, setFilters] = useState({
        search: "",
        from_date: dayjs(),
        to_date: dayjs(),
        page: 1,
        limit: 10,
        sort: "DESC",
        filter_period: { value: "today", label: "Today" }
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "custom", label: "Custom Range" },
        { value: "all", label: "All Records" }
    ];

    const loadCreditNotes = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const params = {
                page: currentFilters.page,
                limit: currentFilters.limit,
                sort: currentFilters.sort,
                filter: currentFilters.filter_period.value,
                search: currentFilters.search,
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();

            const res = await Handler({ method: "GET", url: `/report/credit_note_report.php?${queryParams}` });

            if (res?.success) {
                setRawResponse(res);
                setPagination({
                    current_page: res.pagination?.current_page || 1,
                    total_pages: res.pagination?.total_pages || 1,
                    total_records: res.pagination?.total_records || 0
                });
            }
        } catch (err) {
            toast.error("Sync Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter_period.value !== 'custom') {
            loadCreditNotes();
        }
    }, [filters.page, filters.sort, filters.filter_period]);

    const handlePeriodChange = (selectedOption) => {
        let from = dayjs();
        let to = dayjs();
        if (selectedOption.value === 'week') from = dayjs().subtract(7, 'day');
        else if (selectedOption.value === 'month') from = dayjs().subtract(1, 'month');
        else if (selectedOption.value === '3months') from = dayjs().subtract(3, 'month');

        const newFilters = { ...filters, filter_period: selectedOption, from_date: from, to_date: to, page: 1 };
        setFilters(newFilters);
        if (selectedOption.value !== 'custom') loadCreditNotes(newFilters);
    };

    const chartData = useMemo(() => {
        const labels = rawResponse?.charts?.bar_chart?.labels || [];
        const values = rawResponse?.charts?.bar_chart?.values || [];
        return labels.map((label, i) => ({ 
            name: label.split('-')[1] || label, 
            impact: values[i],
            fullName: label 
        })).slice(0, 10);
    }, [rawResponse]);

    const summary = rawResponse?.summary || {};
    const reportData = rawResponse?.data || [];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: "10px",
            minHeight: "45px",
            border: "1px solid #dee2e6",
            boxShadow: "none"
        })
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                {/* Header */}
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Credit Analytics</h3>
                        <p className="text-muted small m-0">Bill reductions and tax adjustments overview.</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center mt-3 mt-md-0">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter_period}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3" onClick={() => loadCreditNotes()}>
                            <FiRefreshCcw />
                        </Button>
                        <Button color="danger" className="px-4 shadow-sm fw-bold rounded-pill" onClick={() => navigate("/creditnotes/add")}>
                            <FiPlus className="me-1" /> New Note
                        </Button>
                    </div>
                </div>

                {/* Summary Cards */}
                <Row className="g-3 mb-4">
                    <Col lg={4}>
                        <StatCard title="Total Credit Notes" value={summary.total_credit_note_count || 0} icon={<FiBarChart2 />} color="#6c757d" bg="#f8f9fa" />
                    </Col>
                    <Col lg={4}>
                        <StatCard title="Tax Adjustments (GST)" value={`₹${summary.total_gst_credit?.toLocaleString() || 0}`} icon={<FiPieChart />} color="#ffc107" bg="#fff9e6" />
                    </Col>
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm rounded-4 h-100 bg-danger text-white">
                            <CardBody className="p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-bold x-small">Cumulative Reduction</small>
                                    <h3 className="fw-bold m-0">₹{summary.total_credit_amount?.toLocaleString() || 0}</h3>
                                </div>
                                <FiTrendingDown fontSize={35} className="opacity-50" />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-4 mb-4">
                    {/* Impact Chart */}
                    <Col lg={7}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted">Note-wise impact</h6>
                                <div style={{ width: '100%', height: 300 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis dataKey="name" fontSize={10} axisLine={false} tickLine={false} />
                                            <YAxis fontSize={10} axisLine={false} tickLine={false} />
                                            <Tooltip cursor={{fill: '#f8f9fa'}} contentStyle={{borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}} />
                                            <Bar dataKey="impact" radius={[5, 5, 0, 0]} barSize={40}>
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#dc3545' : '#fb919c'} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Advanced Search */}
                    <Col lg={5}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted">Advanced Filters</h6>
                                <Form onSubmit={(e) => { e.preventDefault(); loadCreditNotes(); }}>
                                    <div className="mb-3">
                                        <Label className="x-small fw-bold">SEARCH CLIENT / INVOICE / NOTE</Label>
                                        <div className="input-group bg-light rounded-3 p-1">
                                            <span className="input-group-text bg-transparent border-0"><FiSearch className="text-muted" /></span>
                                            <Input className="bg-transparent border-0" placeholder="Type keyword..." value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} />
                                        </div>
                                    </div>
                                    {filters.filter_period.value === 'custom' && (
                                        <Row className="g-2 mb-3">
                                            <Col md={6}>
                                                <Label className="x-small fw-bold">FROM DATE</Label>
                                                <DatePicker format="DD/MM/YYYY" value={filters.from_date} onChange={(val) => setFilters({...filters, from_date: val})} sx={{width: '100%', '& .MuiOutlinedInput-root': {borderRadius: '10px', backgroundColor: '#f8f9fa'}}} />
                                            </Col>
                                            <Col md={6}>
                                                <Label className="x-small fw-bold">TO DATE</Label>
                                                <DatePicker format="DD/MM/YYYY" value={filters.to_date} minDate={filters.from_date} onChange={(val) => setFilters({...filters, to_date: val})} sx={{width: '100%', '& .MuiOutlinedInput-root': {borderRadius: '10px', backgroundColor: '#f8f9fa'}}} />
                                            </Col>
                                        </Row>
                                    )}
                                    <Button color="primary" block className="fw-bold py-2 shadow-sm rounded-3 mt-2" type="submit" style={{background: '#2e4a3d', border: 'none'}}>Apply Analytics</Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Table */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted small text-uppercase ls-1">
                                    <th className="ps-4 py-3">Reference</th>
                                    <th>Adjustments Impact</th>
                                    <th className="text-end pe-4">Final Settlement</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.length > 0 ? (
                                    reportData.map((item) => (
                                        <tr key={item.credit_note_id}>
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold text-dark text-uppercase">{item.client?.name}</div>
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    <Badge color="soft-danger" className="text-danger">#{item.invoice?.invoice_no}</Badge>
                                                    <small className="text-muted x-small">Note: {item.credit_note_no}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="small"><span className="text-muted">TDS:</span> <span className="fw-bold text-warning">₹{item.analytics?.tds_deducted?.toLocaleString()}</span></div>
                                                    <div className="small"><span className="text-muted">Adj:</span> <span className="fw-bold text-danger">₹{item.credit_note?.total_credit?.toLocaleString()}</span></div>
                                                </div>
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="fw-bold text-success fs-5">₹{item.analytics?.final_net_after_credit?.toLocaleString()}</div>
                                                <div className="x-small text-muted">Original: ₹{item.invoice?.invoice_total?.toLocaleString()}</div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="3" className="py-5 text-center text-muted italic">No analytics data found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination.total_pages)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters.page >= pagination.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
        <CardBody className="d-flex align-items-center gap-3 p-4">
            <div style={{ width: '50px', height: '50px', borderRadius: '14px', backgroundColor: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>
                {icon}
            </div>
            <div>
                <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                <h4 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h4>
            </div>
        </CardBody>
    </Card>
);

export default CreditNoteReportList;